import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { saveBlobResponse } from '../utils/download';
import LocationSelect from './LocationSelect';
import OrgPrintChrome from './OrgPrintChrome';
import { GstRegistrationModal, useGstEnabled } from './GstRegistration';
import { hasAnyRole } from '../utils/permissionUtils';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const toArray = (v) => (Array.isArray(v) ? v : []);
const DEADLINE_BADGE = { PAST_DEADLINE: ['danger', 'Past deadline'], DUE_SOON: ['warning', 'Due soon'], OK: ['success', 'Within time'] };

const currentFinancialYear = () => {
  const d = new Date();
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
};

/**
 * Job work: goods sent to a job worker under a delivery challan (not a sale), what is still with them, what
 * comes back, and the quarterly ITC-04 working.
 */
function JobWork({ active }) {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [returning, setReturning] = useState(null);
  const [ewayOrder, setEwayOrder] = useState(null);
  const canMoveGoods = hasAnyRole(user, ['ORG_ADMIN', 'INV_OPR', 'INV_MGR', 'PROD_OPR', 'PROD_MGR']);
  const gstEnabled = useGstEnabled(active && canMoveGoods);
  const [currency, setCurrency] = useState('');
  const [fy, setFy] = useState(currentFinancialYear());
  const [quarter, setQuarter] = useState(1);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([axios.get('/api/job-work'), axios.get('/api/job-work/pending')])
      .then(([o, p]) => { if (!cancelled) { setOrders(toArray(o.data)); setPending(toArray(p.data)); } })
      .catch(() => { if (!cancelled) toast.error('Error fetching job work'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, reloadKey]);

  useEffect(() => {
    if (!active || !user?.organizationId) return;
    axios.get(`/api/organizations/${user.organizationId}`).then((r) => setCurrency(r.data?.defaultCurrency || '')).catch(() => {});
  }, [active, user?.organizationId]);

  const reload = () => setReloadKey((k) => k + 1);
  const money = (v) => `${currency} ${num(v).toFixed(2)}`;

  const downloadItc04 = async () => {
    try {
      const response = await axios.get('/api/job-work/itc04', { params: { financialYear: fy, quarter }, responseType: 'blob' });
      saveBlobResponse(response, `itc04-${fy}-q${quarter}.zip`);
    } catch (error) {
      let data = error.response?.data;
      if (data instanceof Blob) data = await data.text();
      toast.error(typeof data === 'string' && data ? data : 'Download failed');
    }
  };

  const pendingByOrder = useMemo(() => {
    const m = {};
    pending.forEach((p) => { m[p.orderId] = (m[p.orderId] || 0) + num(p.pending); });
    return m;
  }, [pending]);

  const openDetail = async (o) => {
    try {
      const r = await axios.get(`/api/job-work/${o.id}`);
      setViewing(r.data);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not load the challan'));
    }
  };

  return (
    <div className="mt-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <p className="text-muted mb-0">
          Goods sent to a job worker go out on a delivery challan, not a sale. Inputs must be back within a year (capital goods three), or they
          count as supplied.
        </p>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Form.Select size="sm" style={{ width: 120 }} value={fy} onChange={(e) => setFy(Number(e.target.value))} title="Financial year">
            {[0, 1, 2, 3].map((i) => { const y = currentFinancialYear() - i; return <option key={y} value={y}>{`FY ${y}-${String(y + 1).slice(2)}`}</option>; })}
          </Form.Select>
          <Form.Select size="sm" style={{ width: 130 }} value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
            <option value={1}>Apr - Jun</option><option value={2}>Jul - Sep</option><option value={3}>Oct - Dec</option><option value={4}>Jan - Mar</option>
          </Form.Select>
          <Button size="sm" variant="outline-secondary" onClick={downloadItc04}>ITC-04 working (zip)</Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>+ New challan</Button>
        </div>
      </div>

      {loading && <div className="d-flex justify-content-center mb-3"><Spinner animation="border" role="status" /></div>}

      <Card className="mb-4">
        <Card.Header><h5 className="mb-0">Still with job workers</h5></Card.Header>
        <Card.Body>
          <Table striped bordered size="sm" responsive>
            <thead>
              <tr>
                <th>Job worker</th><th>Challan</th><th>Goods</th><th className="text-end">Sent</th><th className="text-end">Still out</th>
                <th className="text-end">Value</th><th>Expected back</th><th>Deadline</th><th />
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => {
                const [bg, label] = DEADLINE_BADGE[p.deadlineStatus] || ['secondary', p.deadlineStatus];
                return (
                  <tr key={`${p.orderId}-${p.description}`}>
                    <td>{p.jobWorker}</td><td>{p.challanNumber}<div className="small text-muted">{formatDate(p.challanDate)}</div></td>
                    <td>{p.description}{p.goodsType === 'CAPITAL_GOODS' && <Badge bg="info" className="ms-1">Capital</Badge>}</td>
                    <td className="text-end">{p.sent} {p.unit}</td><td className="text-end"><strong>{p.pending} {p.unit}</strong></td>
                    <td className="text-end">{money(p.pendingValue)}</td>
                    <td>{formatDate(p.expectedReturnDate) || '-'}{p.expectedOverdue && <Badge bg="danger" className="ms-1">Late</Badge>}</td>
                    <td><Badge bg={bg} text={bg === 'warning' ? 'dark' : undefined}>{label}</Badge><div className="small text-muted">{p.returnDeadline}</div></td>
                    <td><Button size="sm" variant="outline-primary" onClick={() => openDetail({ id: p.orderId })}>Open</Button></td>
                  </tr>
                );
              })}
              {pending.length === 0 && <tr><td colSpan="9" className="text-center text-muted">Nothing is out with a job worker.</td></tr>}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><h5 className="mb-0">Challans</h5></Card.Header>
        <Card.Body>
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr><th>Challan</th><th>Date</th><th>Job worker</th><th>Job work</th><th>Sent from</th><th className="text-end">Value sent</th><th className="text-end">Still out</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td><strong>{o.orderNumber}</strong></td><td>{formatDate(o.orderDate)}</td><td>{o.jobWorkerName}</td><td>{o.natureOfJobWork}</td>
                  <td>{o.issuingLocationName}</td>
                  <td className="text-end">{money((o.lines || []).reduce((s, l) => s + num(l.taxableValue), 0))}</td>
                  <td className="text-end">{pendingByOrder[o.id] ? num(pendingByOrder[o.id]).toFixed(3).replace(/\.?0+$/, '') : '-'}</td>
                  <td><Badge bg={o.status === 'COMPLETED' ? 'success' : 'warning'} text={o.status === 'COMPLETED' ? undefined : 'dark'}>{o.status === 'COMPLETED' ? 'Closed' : 'Open'}</Badge></td>
                  <td className="text-nowrap">
                    <Button size="sm" variant="outline-info" onClick={() => openDetail(o)}>Open</Button>
                    {canMoveGoods && gstEnabled.ewayEnabled && (
                      <Button size="sm" variant="outline-secondary" className="ms-1" title="E-way bill for the goods sent" onClick={() => setEwayOrder(o)}>e-Way</Button>
                    )}
                    <HistoryButton documentType="JobWorkOrder" documentId={o.id} label={`Job work challan ${o.orderNumber}`} className="ms-1" />
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="9" className="text-center text-muted">No challans yet.</td></tr>}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <CreateChallan show={showCreate} currency={currency} onHide={() => setShowCreate(false)} onCreated={reload} />
      <ChallanDetail order={viewing} money={money} onHide={() => setViewing(null)} onReturn={(o) => { setViewing(null); setReturning(o); }} />
      <RecordReturn order={returning} onHide={() => setReturning(null)} onRecorded={reload} />
      <GstRegistrationModal
        show={Boolean(ewayOrder)}
        docType="JOB_WORK"
        docId={ewayOrder?.id}
        label={ewayOrder?.orderNumber}
        apiBase={ewayOrder ? `/api/eway-movements/job-work/${ewayOrder.id}` : undefined}
        onHide={() => setEwayOrder(null)}
        canCancel={hasAnyRole(user, ['ORG_ADMIN', 'INV_MGR', 'PROD_MGR'])}
      />
    </div>
  );
}

function CreateChallan({ show, currency, onHide, onCreated }) {
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [retailStores, setRetailStores] = useState([]);
  const [productionUnits, setProductionUnits] = useState([]);
  const [lots, setLots] = useState([]);
  const [form, setForm] = useState({ vendorId: '', orderDate: '', expectedReturnDate: '', nature: '', notes: '', location: '' });
  const [lines, setLines] = useState([{ lotId: '', quantity: '', unitValue: '', taxRate: '', goodsType: 'INPUTS' }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setForm({ vendorId: '', orderDate: '', expectedReturnDate: '', nature: '', notes: '', location: '' });
    setLines([{ lotId: '', quantity: '', unitValue: '', taxRate: '', goodsType: 'INPUTS' }]);
    setLots([]);
    axios.get('/api/inventory/vendors/active').then((r) => setVendors(toArray(r.data))).catch(() => {});
    axios.get('/api/inventory/warehouses').then((r) => setWarehouses(toArray(r.data))).catch(() => {});
    axios.get('/api/inventory/retail-stores').then((r) => setRetailStores(toArray(r.data))).catch(() => {});
    axios.get('/api/inventory/production-units/active').then((r) => setProductionUnits(toArray(r.data))).catch(() => {});
  }, [show]);

  useEffect(() => {
    if (!form.location) { setLots([]); return; }
    const [locationType, locationId] = form.location.split(':');
    axios.get('/api/inventory/inventory-lots', { params: { locationType, locationId } })
      .then((r) => setLots(toArray(r.data)))
      .catch(() => setLots([]));
  }, [form.location]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const lotOf = (id) => lots.find((l) => String(l.id) === String(id));

  const submit = async () => {
    if (!form.location) { toast.error('Choose where the goods are sent from'); return; }
    const [issuingLocationType, issuingLocationId] = form.location.split(':');
    try {
      setSaving(true);
      const response = await axios.post('/api/job-work', {
        vendorId: form.vendorId ? Number(form.vendorId) : null,
        orderDate: form.orderDate || null,
        expectedReturnDate: form.expectedReturnDate || null,
        natureOfJobWork: form.nature,
        notes: form.notes,
        issuingLocationType,
        issuingLocationId: Number(issuingLocationId),
        lines: lines.filter((l) => l.lotId).map((l) => ({
          lotId: Number(l.lotId),
          quantity: l.quantity === '' ? null : num(l.quantity),
          unitValue: l.unitValue === '' ? null : num(l.unitValue),
          taxRatePercent: l.taxRate === '' ? null : num(l.taxRate),
          goodsType: l.goodsType
        }))
      });
      toast.success(`Challan ${response.data.orderNumber} created and stock issued`);
      onCreated();
      onHide();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to create the challan'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton><Modal.Title>New job work challan</Modal.Title></Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="py-2 small">
          Creating the challan takes the goods out of stock straight away. It is a delivery challan, not a sale: no invoice and no GST
          is charged. Record what comes back, and the job worker&apos;s own charges (a purchase invoice), separately.
        </Alert>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Job worker</Form.Label>
              <Form.Select value={form.vendorId} onChange={(e) => set({ vendorId: e.target.value })}>
                <option value="">-- Choose --</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Nature of job work</Form.Label>
              <Form.Control value={form.nature} placeholder="e.g. polishing, dyeing, packing" onChange={(e) => set({ nature: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Sent from</Form.Label>
              <LocationSelect value={form.location} onChange={(e) => { set({ location: e.target.value }); setLines([{ lotId: '', quantity: '', unitValue: '', taxRate: '', goodsType: 'INPUTS' }]); }}
                warehouses={warehouses} retailStores={retailStores} productionUnits={productionUnits} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Challan date (blank = today)</Form.Label>
              <DateInput value={form.orderDate} onChange={(e) => set({ orderDate: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Expected back by</Form.Label>
              <DateInput value={form.expectedReturnDate} min={form.orderDate} onChange={(e) => set({ expectedReturnDate: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control value={form.notes} maxLength={1000} onChange={(e) => set({ notes: e.target.value })} />
            </Form.Group>
          </Col>
        </Row>

        <Table bordered size="sm" responsive>
          <thead>
            <tr><th style={{ minWidth: 280 }}>Goods (inventory lot)</th><th style={{ width: 110 }}>Quantity</th><th style={{ width: 120 }}>Value / unit</th>
              <th style={{ width: 90 }}>GST %</th><th style={{ width: 150 }}>Type</th><th className="text-end">Value</th><th style={{ width: 40 }} /></tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const lot = lotOf(l.lotId);
              const unitValue = l.unitValue !== '' ? num(l.unitValue) : num(lot?.landedCostPerUnit);
              return (
                <tr key={i}>
                  <td>
                    <Form.Select size="sm" value={l.lotId} disabled={!form.location} onChange={(e) => setLine(i, { lotId: e.target.value })}>
                      <option value="">{form.location ? 'Choose a lot' : 'Choose the location first'}</option>
                      {lots.map((x) => (
                        <option key={x.id} value={x.id}>{`${x.itemName} · lot ${x.internalLotNumber || x.id} · ${x.availableQuantity} ${x.uomSymbol || ''} available`}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td><Form.Control size="sm" type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                  <td><Form.Control size="sm" type="number" min="0" step="0.01" value={l.unitValue} placeholder={lot ? String(num(lot.landedCostPerUnit).toFixed(2)) : ''} onChange={(e) => setLine(i, { unitValue: e.target.value })} /></td>
                  <td><Form.Control size="sm" type="number" min="0" max="100" step="any" value={l.taxRate} onChange={(e) => setLine(i, { taxRate: e.target.value })} /></td>
                  <td>
                    <Form.Select size="sm" value={l.goodsType} onChange={(e) => setLine(i, { goodsType: e.target.value })}>
                      <option value="INPUTS">Inputs (back in 1 year)</option><option value="CAPITAL_GOODS">Capital goods (3 years)</option>
                    </Form.Select>
                  </td>
                  <td className="text-end">{currency} {(num(l.quantity) * unitValue).toFixed(2)}</td>
                  <td>{lines.length > 1 && <Button size="sm" variant="outline-danger" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>&times;</Button>}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-secondary" onClick={() => setLines((p) => [...p, { lotId: '', quantity: '', unitValue: '', taxRate: '', goodsType: 'INPUTS' }])}>+ Add goods</Button>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create challan and issue stock'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function ChallanDetail({ order, money, onHide, onReturn }) {
  const o = order;
  const open = o && o.status === 'OPEN';
  return (
    <Modal show={Boolean(o)} onHide={onHide} size="xl" className="print-modal">
      <Modal.Header className="print-header">
        <Modal.Title>Delivery Challan{o ? ` — ${o.orderNumber}` : ''}</Modal.Title>
        <div className="print-actions">
          {open && <Button variant="warning" className="me-2" onClick={() => onReturn(o)}>Record goods back</Button>}
          <Button variant="primary" className="me-2" onClick={() => window.print()}>Print</Button>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </div>
      </Modal.Header>
      <Modal.Body className="print-body">
        <OrgPrintChrome>
          {o && (
            <div className="print-content">
              <div className="text-center mb-3">
                <h2>DELIVERY CHALLAN</h2>
                <p className="mb-0"><strong>{o.orderNumber}</strong></p>
                <p className="text-muted small">Goods sent for job work — not a sale</p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p className="mb-1"><strong>Principal GSTIN:</strong> {o.principalGstin || '-'}</p>
                  <p className="mb-1"><strong>Sent from:</strong> {o.issuingLocationName}</p>
                  <p className="mb-1"><strong>Date:</strong> {formatDate(o.orderDate)}</p>
                  <p className="mb-1"><strong>Expected back by:</strong> {formatDate(o.expectedReturnDate) || '-'}</p>
                </Col>
                <Col md={6}>
                  <p className="mb-1"><strong>Job worker:</strong> {o.jobWorkerName}</p>
                  <p className="mb-1"><strong>Address:</strong> {o.jobWorkerAddress || '-'}</p>
                  <p className="mb-1"><strong>Job worker GSTIN:</strong> {o.jobWorkerGstin || '-'}{o.jobWorkerStateCode && ` (state ${o.jobWorkerStateCode})`}</p>
                  <p className="mb-1"><strong>Nature of job work:</strong> {o.natureOfJobWork}</p>
                </Col>
              </Row>
              <Table bordered size="sm">
                <thead>
                  <tr><th>Goods</th><th>HSN</th><th className="text-end">Quantity</th><th>Type</th><th className="text-end">Value</th><th className="text-end">GST %</th>
                    <th className="text-end">Back</th><th className="text-end">Waste</th><th className="text-end">Still out</th><th>Back by</th></tr>
                </thead>
                <tbody>
                  {(o.lines || []).map((l) => (
                    <tr key={l.id}>
                      <td>{l.description}<div className="small text-muted">lot {l.lotNumber || l.lotId}</div></td><td>{l.hsnCode || '-'}</td>
                      <td className="text-end">{l.quantity} {l.unitSymbol}</td>
                      <td>{l.goodsType === 'CAPITAL_GOODS' ? 'Capital goods' : 'Inputs'}</td>
                      <td className="text-end">{money(l.taxableValue)}</td><td className="text-end">{l.taxRatePercent ?? '-'}</td>
                      <td className="text-end">{l.returnedQuantity}</td><td className="text-end">{l.wasteQuantity}</td>
                      <td className="text-end"><strong>{l.pendingQuantity}</strong></td><td>{l.returnDeadline}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <p className="small">
                Total value of goods sent: <strong>{money((o.lines || []).reduce((s, l) => s + num(l.taxableValue), 0))}</strong>.
                Inputs to be received back within one year, and capital goods within three, of the challan date.
              </p>
              {(o.receipts || []).length > 0 && (
                <>
                  <h6 className="mt-3">Received back</h6>
                  <Table bordered size="sm">
                    <thead><tr><th>Receipt</th><th>Date</th><th>Job worker&apos;s reference</th><th>Goods back</th><th>Stock receipt</th></tr></thead>
                    <tbody>
                      {o.receipts.map((r) => (
                        <tr key={r.id}>
                          <td>{r.receiptNumber}</td><td>{formatDate(r.receiptDate)}</td><td>{r.jobWorkerReference || '-'}</td>
                          <td>{(r.lines || []).map((rl) => {
                            const parts = [];
                            if (num(rl.outputQuantity) > 0) parts.push(`${rl.outputQuantity} ${rl.outputItemName || ''} back for ${rl.inputQuantityConsumed} ${rl.inputDescription}`);
                            else if (num(rl.inputQuantityConsumed) > 0) parts.push(`${rl.inputQuantityConsumed} ${rl.inputDescription} accounted for, nothing back`);
                            if (num(rl.wasteQuantity) > 0) parts.push(`${rl.wasteQuantity} ${rl.inputDescription} wasted`);
                            return parts.join(', ');
                          }).join('; ')}</td>
                          <td>{r.grnNumber || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
              {o.notes && <p className="mt-2"><strong>Notes:</strong> {o.notes}</p>}
              <div className="d-flex justify-content-between mt-5 pt-4">
                <span>Authorised signatory</span><span>Receiver&apos;s signature</span>
              </div>
            </div>
          )}
        </OrgPrintChrome>
      </Modal.Body>
    </Modal>
  );
}

function RecordReturn({ order, onHide, onRecorded }) {
  const [items, setItems] = useState([]);
  const [entries, setEntries] = useState({});
  const [date, setDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order) return;
    setEntries({}); setDate(''); setReference(''); setNotes('');
    Promise.all([axios.get('/api/products'), axios.get('/api/organization-materials')])
      .then(([p, m]) => {
        const map = new Map();
        toArray(p.data).forEach((x) => { if (x.item?.id) map.set(x.item.id, x.name || x.item.name); });
        toArray(m.data).forEach((x) => { if (x.item?.id && !map.has(x.item.id)) map.set(x.item.id, x.item.name); });
        setItems([...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name))));
      })
      .catch(() => {});
  }, [order]);

  const lines = (order?.lines || []).filter((l) => num(l.pendingQuantity) > 0);
  const setEntry = (id, patch) => setEntries((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const submit = async () => {
    const payload = lines.map((l) => {
      const e = entries[l.id] || {};
      return {
        orderLineId: l.id,
        outputItemId: e.outputItemId ? Number(e.outputItemId) : (num(e.output) > 0 ? l.itemId : null),
        outputQuantity: num(e.output),
        inputQuantityConsumed: num(e.consumed),
        wasteQuantity: num(e.waste),
        remarks: e.remarks || null
      };
    }).filter((l) => l.outputQuantity > 0 || l.inputQuantityConsumed > 0 || l.wasteQuantity > 0);
    if (payload.length === 0) { toast.error('Enter what came back on at least one line'); return; }
    try {
      setSaving(true);
      await axios.post(`/api/job-work/${order.id}/returns`, { receiptDate: date || null, jobWorkerReference: reference, notes, lines: payload });
      toast.success('Return recorded and stock received');
      onRecorded();
      onHide();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to record the return'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={Boolean(order)} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton><Modal.Title>Goods back — {order?.orderNumber}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="py-2 small">
          Say what came back and how much of what you sent it accounts for. Finished goods can be a different item from the inputs (cane in,
          jaggery out). Goods received go back into stock where they were sent from, valued at the input cost; they&apos;re held for quality
          check like any other receipt.
        </Alert>
        <Row className="g-3 mb-3">
          <Col md={3}><Form.Group><Form.Label>Received on (blank = today)</Form.Label><DateInput value={date} min={order?.orderDate} onChange={(e) => setDate(e.target.value)} /></Form.Group></Col>
          <Col md={4}><Form.Group><Form.Label>Job worker&apos;s challan / invoice no.</Form.Label><Form.Control value={reference} maxLength={60} onChange={(e) => setReference(e.target.value)} /></Form.Group></Col>
          <Col md={5}><Form.Group><Form.Label>Notes (optional)</Form.Label><Form.Control value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} /></Form.Group></Col>
        </Row>
        <Table bordered size="sm" responsive>
          <thead>
            <tr><th>Sent</th><th className="text-end">Still out</th><th style={{ minWidth: 200 }}>Goods that came back</th><th style={{ width: 110 }}>Quantity back</th>
              <th style={{ width: 130 }}>Input accounted for</th><th style={{ width: 110 }}>Wasted / lost</th></tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const e = entries[l.id] || {};
              return (
                <tr key={l.id}>
                  <td>{l.description}</td>
                  <td className="text-end">{l.pendingQuantity} {l.unitSymbol}</td>
                  <td>
                    <Form.Select size="sm" value={e.outputItemId || ''} onChange={(ev) => setEntry(l.id, { outputItemId: ev.target.value })}>
                      <option value="">{`Same as sent (${l.description})`}</option>
                      {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </Form.Select>
                  </td>
                  <td><Form.Control size="sm" type="number" min="0" step="any" value={e.output || ''} onChange={(ev) => setEntry(l.id, { output: ev.target.value })} /></td>
                  <td><Form.Control size="sm" type="number" min="0" step="any" value={e.consumed || ''} onChange={(ev) => setEntry(l.id, { consumed: ev.target.value })} /></td>
                  <td><Form.Control size="sm" type="number" min="0" step="any" value={e.waste || ''} onChange={(ev) => setEntry(l.id, { waste: ev.target.value })} /></td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={saving}>{saving ? 'Recording...' : 'Record return'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default JobWork;
